<script setup lang="ts">
import type { CustomElementsDependencies } from '~/types/custom-elements'

import { useHost, useAttrs, computed, ref, useSlots, watch } from 'vue'
import type {
  TypeEventSkeleton,
  TypeEventWithoutUnresolvableLinksResponse,
} from '~/types/contentful'
import BaseButton from '~/components/BaseButton.vue'

// without below, vue will warn about extraneous attributes when passing data-* attributes to the component
defineOptions({
  inheritAttrs: false,
})

const attrs = useAttrs()

declare global {
  interface Window {
    __WPN_CUSTOM_ELEMENTS__?: CustomElementsDependencies
  }
}

const props = withDefaults(
  defineProps<{
    entry: string
    linkSource?: 'eventLink' | 'eventLink2'
    labelKey?: string
    target?: '_blank' | '_self' | '_parent' | '_top'
    size?: 'default' | 'large' | 'small'
    presentation?:
      | 'default'
      | 'dark'
      | 'light'
      | 'invisible'
      | 'strip'
      | 'two-tones'
      | 'clear'
      | 'outline'
      | 'translucent'
      | 'orange'
  }>(),
  {
    linkSource: 'eventLink',
    labelKey: 'schedule-events',
    target: '_self',
    size: 'default',
    presentation: 'orange',
  },
)

type WpnEventHost = HTMLElement & {
  _slots?: Record<string, Node[]>
}

const host = useHost() as WpnEventHost | null
const event = ref<TypeEventWithoutUnresolvableLinksResponse>()

const { localizedStrings } = useLocalizedStrings()

const slots = useSlots()

const slotLabel = computed(() => {
  const hostSlotText = host?._slots?.default
    ?.map((node) => node.textContent || '')
    .join('')
    .trim()

  if (hostSlotText) return hostSlotText

  const text = slots
    .default?.()
    .map((vnode) => (typeof vnode.children === 'string' ? vnode.children : ''))
    .join('')
    .trim()

  return text || undefined
})

const btnLabel = computed(() => {
  if (slotLabel.value) return slotLabel.value

  const defaultLabel =
    localizedStrings.value['schedule-events'] || 'Schedule Events'

  return localizedStrings.value[props.labelKey] || defaultLabel
})

const scheduleInfo = computed(() => {
  if (!event.value) return undefined

  if (props.linkSource === 'eventLink2') {
    return {
      scheduleId: event.value.fields.eventScheduleId2,
      link: event.value.fields.eventLink2,
    }
  }

  return {
    scheduleId: event.value.fields.eventScheduleId,
    link: event.value.fields.eventLink,
  }
})

const handleScheduleClick = () => {
  if (!scheduleInfo.value?.link) return

  pushToGaDataLayer({
    event: 'ga4.trackEvent',
    eventName: 'schedule_click',
    eventParams: {
      link_text: btnLabel.value,
      link_url: scheduleInfo.value.link,
      entry_title: 'WPN-EVENT-CTA',
      parent_entry_title: event.value?.fields?.entryTitle || null,
    },
  })

  window.open(scheduleInfo.value.link, props.target)
}

async function loadEvent() {
  event.value = undefined

  if (!props.entry || !window.__WPN_CUSTOM_ELEMENTS__) return

  try {
    const deps = window.__WPN_CUSTOM_ELEMENTS__
    const locale = deps.getLocale()

    const response =
      await deps.client.withoutUnresolvableLinks.getEntries<TypeEventSkeleton>({
        content_type: 'event',
        include: 0,
        limit: 1,
        locale,
        'sys.id': props.entry,
        select: [
          'sys.id',
          'fields.entryTitle',
          'fields.title',
          'fields.eventLink',
          'fields.eventScheduleId',
          'fields.eventLink2',
          'fields.eventScheduleId2',
        ],
      })

    event.value = response.items[0]
  } catch (error) {
    console.warn('[wpn-event-btn] failed to load entry', {
      entry: props.entry,
      error,
    })
  }
}

watch(
  () => props.entry,
  () => {
    loadEvent()
  },
  { immediate: true },
)

const userStore = useUserStore()

const { stores } = storeToRefs(userStore)

const scheduleDropdownVisible = computed(() => {
  const url = scheduleInfo.value?.link
  if (!url) return false

  try {
    const parsedUrl = new URL(url)
    return (
      parsedUrl.hostname === 'eventlink.wizards.com' && stores.value.length > 1
    )
  } catch {
    return false
  }
})

const scheduledAllStores = (scheduleId: string | undefined) => {
  if (!scheduleId) return false
  if (!stores.value.length) return false

  return stores.value.every(
    (s) =>
      !!s?.templateUsages?.find(
        (t) => t?.templateId === scheduleId && (t?.count ?? 0) > 0,
      ),
  )
}
</script>

<template>
  <ScheduleDropdown
    v-if="event && scheduleDropdownVisible"
    :href="scheduleInfo?.link"
    :label="btnLabel"
    :parent-entry-title="event?.fields?.entryTitle"
    :presentation="props.presentation"
    :size="props.size"
    :target="props.target"
    :schedule-id="scheduleInfo?.scheduleId"
    :scheduled-all-stores="scheduledAllStores(scheduleInfo?.scheduleId)"
    v-bind="attrs"
  />
  <BaseButton
    v-else-if="event"
    v-bind="attrs"
    :href="scheduleInfo?.link"
    :presentation="props.presentation"
    :target="props.target"
    :size="props.size"
    @click.prevent="handleScheduleClick"
  >
    <IconCheckmark
      v-if="scheduledAllStores(scheduleInfo?.scheduleId)"
      style="color: var(--green); width: 20px; height: 20px; margin-right: 10px"
    />{{ btnLabel }}
  </BaseButton>
</template>
